"use client";

import Link from "next/link";
import { Fragment } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Eye, Heart, User, Calendar } from "lucide-react";
import { getMediaUrl } from "@/lib/api";
import { buildMemeGeneratorUrl, generateMemeTemplateUrl } from "@/lib/utils";
import { formatDistanceToNowStrict } from "date-fns";

const getCleanTemplateTitle = (title: string) => {
  const cleaned = title
    .replace(/meme[-\s]*template/gi, " ")
    .replace(/\btemplate\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || title;
};

interface MemeTemplateCardProps {
  template: any;
  viewMode?: "grid" | "list";
  onAddCaption?: (template: any) => void;
}

export default function MemeTemplateCard({
  template,
  viewMode = "grid",
  onAddCaption,
}: MemeTemplateCardProps) {
  const detailUrl = generateMemeTemplateUrl(template.id, template.title);
  const imageUrl = getMediaUrl(template.image_url || template.image_path);
  const cleanTitle = getCleanTemplateTitle(template.title || "meme");
  const memeGeneratorUrl = buildMemeGeneratorUrl(
    template.id,
    cleanTitle || template.title || "meme"
  );

  if (viewMode === "grid") {
    return (
      <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all duration-200 group overflow-hidden flex flex-col h-full">
        <CardContent className="p-0 flex flex-col h-full">
          {/* Wrapper Link for image & text so they can right-click and open in a new tab */}
          <Link href={detailUrl} className="block flex-1 group">
            {/* Template Image */}
            <div className="relative aspect-square overflow-hidden rounded-t-lg">
              <img
                src={imageUrl}
                alt={template.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200" />
            </div>

            {/* Template Info */}
            <div className="p-4 pb-2">
              <h3 className="font-semibold text-white mb-2 line-clamp-2 group-hover:text-red-400 transition-colors">
                {template.title}
              </h3>

              <div className="flex items-center justify-between text-sm text-zinc-400 mb-2">
                <div className="flex items-center gap-1">
                  <Eye className="h-3 w-3" />
                  <span>{template.no_of_views || 0}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Heart className="h-3 w-3" />
                  <span>{template.likes || 0}</span>
                </div>
              </div>
            </div>
          </Link>

          {/* Caption Button (outside the Link to prevent invalid HTML nesting) */}
          {onAddCaption && (
            <div className="px-4 pb-4">
              <Link
                href={memeGeneratorUrl}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="w-full h-9 bg-red-600 hover:bg-red-700 text-white text-xs rounded-md flex items-center justify-center font-semibold transition-colors duration-200"
              >
                Add Caption
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // List View
  return (
    <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all duration-200 group overflow-hidden">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Wrapper Link for Left Thumbnail Image */}
          <Link
            href={detailUrl}
            className="block relative w-24 h-24 overflow-hidden rounded-lg flex-shrink-0"
          >
            <img
              src={imageUrl}
              alt={template.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            />
          </Link>

          {/* Template Info & CTA */}
          <div className="flex-1 min-w-0 w-full">
            <Link href={detailUrl} className="block group">
              <h3 className="font-semibold text-white mb-2 group-hover:text-red-400 transition-colors">
                {template.title}
              </h3>
            </Link>

            {template.description && (
              <p className="text-zinc-400 text-sm mb-2 line-clamp-2">
                {template.description}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400 mb-2">
              <div className="flex items-center gap-1">
                <User className="h-3 w-3" />
                <span>{template.username || template.user?.username || "Anonymous"}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>
                  {formatDistanceToNowStrict(
                    new Date(template.created_at || new Date()),
                    { addSuffix: true }
                  )}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                <span>{template.no_of_views || 0}</span>
              </div>
              <div className="flex items-center gap-1">
                <Heart className="h-3 w-3" />
                <span>{template.likes || 0}</span>
              </div>
            </div>

            {onAddCaption && (
              <Link
                href={memeGeneratorUrl}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="inline-flex items-center justify-center h-9 px-4 bg-red-600 hover:bg-red-700 text-white text-xs rounded-md font-semibold transition-colors duration-200 mt-2"
              >
                Add Caption
              </Link>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
